import { cva, type VariantProps } from "class-variance-authority";
import { useId, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "./cn";

/**
 * Estilo do campo, portado do `input.tsx` do CRM.
 *
 * O que vale notar: o estado inválido é dirigido por `aria-invalid`, não por uma
 * prop de cor. Assim a borda vermelha e o anúncio no leitor de tela vêm sempre
 * juntos — não existe estado visual de erro sem o atributo que o comunica.
 */
export const variantesDoCampo = cva(
  [
    "w-full text-sm outline-none transition-[color,box-shadow,border-color] duration-200",
    "placeholder:text-muted-foreground",
    "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
    "read-only:cursor-not-allowed read-only:bg-muted/50 read-only:text-muted-foreground",
    "aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/20",
  ],
  {
    variants: {
      variante: {
        padrao: [
          "rounded-lg border border-input bg-card px-3.5 py-2.5 shadow-sm",
          "hover:border-input/80",
          "focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/25",
        ],
        sublinhado: [
          "rounded-none border-0 border-b-2 border-input bg-transparent px-0 py-2",
          "focus-visible:border-primary focus-visible:ring-0",
        ],
      },
      tamanho: {
        medio: "h-11",
        pequeno: "h-9 px-3 py-2",
      },
    },
    defaultVariants: { variante: "padrao", tamanho: "medio" },
  },
);

export type PropsDoCampoDeTexto = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "className" | "id"
> &
  VariantProps<typeof variantesDoCampo> & {
    readonly rotulo: string;
    /**
     * Mensagens de erro do campo. Aceita várias: o servidor pode devolver mais de
     * uma para o mesmo campo.
     *
     * O `| undefined` é explícito por causa de `exactOptionalPropertyTypes` no
     * tsconfig — sem ele, passar `erros={talvezUndefined}` não compila e o
     * chamador teria que espalhar `...(x ? { erros: x } : {})` em cada campo.
     */
    readonly erros?: readonly string[] | undefined;
    /** Texto de apoio, mostrado quando não há erro. */
    readonly dica?: string | undefined;
    /** Ícone à esquerda (lucide). Recua o texto para não sobrepor. */
    readonly icone?: ReactNode;
    readonly className?: string | undefined;
  };

/**
 * Campo de texto do design system.
 *
 * Concentra a acessibilidade que quase sempre falta em formulário feito à mão:
 *
 * - `label` ligada ao input por `htmlFor`/`id` gerado — clicar no rótulo foca o
 *   campo, e o leitor de tela anuncia o nome junto do valor;
 * - `aria-invalid` e `aria-describedby` apontando para a mensagem, para o erro ser
 *   anunciado em vez de só ficar vermelho;
 * - `role="alert"` no erro, para quem usa leitor de tela ouvir a mudança.
 *
 * Erro vermelho sem `aria-invalid` é invisível para quem não vê a cor — e é o
 * padrão que sai quando cada tela improvisa o seu campo.
 */
export function CampoDeTexto({
  rotulo,
  erros,
  dica,
  icone,
  variante,
  tamanho,
  className,
  required,
  ...resto
}: PropsDoCampoDeTexto) {
  const id = useId();
  const idDoErro = `${id}-erro`;
  const idDaDica = `${id}-dica`;
  const temErro = erros !== undefined && erros.length > 0;

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={id} className="text-sm font-medium leading-none">
        {rotulo}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-destructive">
            *
          </span>
        ) : null}
      </label>

      <div className="relative">
        {icone ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground [&_svg]:size-4"
          >
            {icone}
          </span>
        ) : null}
        <input
          {...resto}
          id={id}
          required={required}
          aria-invalid={temErro}
          aria-describedby={temErro ? idDoErro : dica ? idDaDica : undefined}
          className={cn(
            variantesDoCampo({ variante, tamanho }),
            icone ? "pl-9" : undefined,
          )}
        />
      </div>

      {temErro ? (
        <p id={idDoErro} role="alert" className="text-xs font-medium text-destructive">
          {erros.join(". ")}
        </p>
      ) : dica ? (
        <p id={idDaDica} className="text-xs text-muted-foreground">
          {dica}
        </p>
      ) : null}
    </div>
  );
}
